import { Injectable, Injector } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { ToastController } from 'providers/toast-controller';
import type { MyApp } from 'app/app.component';
import * as Sentry from '@sentry/angular';
import { TelemetryService } from './telemetry.service';
import { TimeoutError, sleep, timeout } from '@cf-shared/shared-util';
import { DatabaseRecoveryOutcome, DatabaseRecoverySettlement } from './singleton-read-recovery';
import type { DatabaseWrapper, DatabaseWrapperProvider } from './database-wrapper';
import type { Toast } from 'components/toast/toast-host';
import { DatabaseErrorActionKind, decideDatabaseErrorAction } from './database-error-families';
import { RECOVERY_REPORTING_SERVICE, RecoveryReportingService } from './recovery-reporting-service';
import type { SentryReportTags } from './problem-kind';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { errorReportFields } from './error-patterns';
import { mayAbandonSilentLocalStore } from './local-store-generation';
import { FatalErrorKind } from 'pages/fatal-error/fatal-error-interface';

export const kDbInRecoveryStorageKey = 'dbInRecovery';

/**
 * Where a `database is closed` error was handed to `interceptDatabaseError` from.
 *
 * It is a TAG on the report, so the set stays small and fixed on purpose — see
 * `SentryReportTags` in problem-kind.ts for why a tag must be low-cardinality. Add a value
 * here when a new call site starts passing one; a call site that passes nothing reports
 * `unknown`, which is what all of the roughly forty existing callers do.
 */
export type ClosedHandleOrigin =
  /** the window's `unhandledrejection` listener — nobody had caught it */
  | 'unhandled-rejection'
  /** Angular's own error handler */
  | 'angular-error-handler'
  /** the singleton pre-sync's error, denied or rejection handler */
  | 'singleton-pre-sync'
  /** the remote/local document-count check that runs before an initial pass */
  | 'sync-doc-count-check'
  /** the main replication's `denied` handler */
  | 'replication-denied'
  /** the main replication's `error` handler */
  | 'replication-error'
  /** the resume pull catch-up's failure handler (#6230) */
  | 'pull-catchup'
  /** a call site that does not name itself yet */
  | 'unknown';

/**
 * What the app does when its local database breaks: decide whether a failure is one we
 * can recover from, try to reopen the databases in place, and reload the page when that
 * does not work.
 *
 * This used to live inside `LoggingService` (df-16fe419d737b). The two jobs are
 * unrelated — one turns a caught error into a Sentry event, a Mixpanel event and maybe a
 * toast; this one owns a lifecycle that can reload the app — so a reader changing either
 * one had to work out which of the two they were standing in first.
 *
 * `LoggingService` used to carry a forwarding method of each name below, so that the
 * split could land without moving forty call sites in the same commit. Those forwarders
 * are gone (df-0630dafeff8d) and every caller injects this service directly.
 */
@Injectable()
export class DatabaseRecoveryService {
  private app: MyApp;
  private telemetry: TelemetryService;
  private dbProvider: DatabaseWrapperProvider;
  /** the reporting service, once `reporting()` has looked it up — see that method */
  private reportingService: RecoveryReportingService;

  constructor(private toastCtrl: ToastController, private injector: Injector) { }

  /** The reporting service we file our findings with, looked up the first time we report
   * something and remembered afterwards.
   *
   * We ask for it rather than taking it through the constructor because the two services call
   * each other: reporting asks us about a database error as a safety net, and we ask reporting
   * to file the problems we find. Taking each other through a constructor would be a dependency
   * cycle Angular cannot build. Asking at the moment we report moves the lookup to a point where
   * both services already exist, so there is no cycle and no ordering question: it does not
   * matter who built whom, or in what order.
   *
   * `RECOVERY_REPORTING_SERVICE` says why this goes through a token rather than naming
   * `LoggingService`. If the lookup fails this answers `undefined` rather than throwing, and
   * every caller here has to cope with that — `reportSilentProblem` below says why. */
  private reporting(): RecoveryReportingService | undefined {
    if (!this.reportingService) {
      try {
        this.reportingService = this.injector.get(RECOVERY_REPORTING_SERVICE);
      } catch (err) {
        console.error('Database recovery could not get hold of a reporting service', err);
      }
    }
    return this.reportingService;
  }

  /** Handed the rest of what this service needs, once the app component exists.
   *
   * The hazard this leaves in place, so nobody is surprised by it: until this method runs,
   * `app`, `telemetry` and `dbProvider` are `undefined` and nothing in the type system says
   * so. That is unchanged from when this code lived in `LoggingService`, which has the same
   * two-phase start-up for the same reason. What protects the early-boot path is that the
   * reporting calls below go through `reportSilentProblem`, which cannot throw. */
  initialize(app: MyApp, telemetry: TelemetryService, dbProvider: DatabaseWrapperProvider) {
    this.app = app;
    this.telemetry = telemetry;
    this.dbProvider = dbProvider;
  }

  /** File a silent problem through the reporting service.
   *
   * Every silent-problem call in this service goes through here so that one guard covers them
   * all. The guard is not about ordering any more — `reporting()` above asks Angular at the
   * moment we report, so there is no window in which we exist and the reporting service does
   * not. It is here because reporting must never be worth a hang: if the lookup somehow fails,
   * reading a property of `undefined` would throw, the throw would escape
   * `interceptDatabaseError` after it has already opened a recovery, and then nothing settles
   * the outcome — so every caller waiting on `whenDatabaseRecoverySettledAsync()` would wait
   * for ever. Nothing else here needs catching: `trackSilentProblem` promises never to throw
   * and keeps that promise itself. */
  private reportSilentProblem(problemName: string, context: any, tags?: SentryReportTags) {
    const reporting = this.reporting();
    if (!reporting) {
      console.error(`Database recovery cannot report "${problemName}" — it has no reporting service`, context);
      return;
    }
    reporting.trackSilentProblem(problemName, context, tags);
  }

  startingDatabaseRecovery = false; // this is set while starting to recover, but didn't restart yet. needed to prevent multiple restarts that recurse into a loop

  /** #4502: what a caller told to wait should hear once the recovery in flight settles.
   * See `DatabaseRecoveryOutcome` for what each answer means and why it is never cleared. */
  private databaseRecoveryOutcome = new DatabaseRecoveryOutcome();
  private lastGracefulRecoveryTime = 0; // timestamp of last successful graceful recovery — cooldown guard
  private purgesInProgress = 0; // how many destroy-bracketed calls are currently running across all DBs
  private wipingUntilReload = false; // set once the sign-out wipe starts deleting databases; never cleared (the page reloads)

  /** Closed-handle errors already reported by `reportClosedDatabaseHandleCallOnce`. One late
   * call reaches that method twice by design — see the method's own comment — and this is what
   * keeps it to one Sentry event. A WeakSet rather than a Set so a reported error object is
   * still collectable; nothing here needs to outlive the error itself. */
  private readonly reportedClosedDatabaseHandleErrors = new WeakSet<object>();

  /** Track that a purge is in progress. While a purge is running, "database is closed"
   * rejections from pending PouchDB ops are expected (we intentionally destroy the local DB)
   * and must not trigger graceful recovery — recovery would race against the purge clearing
   * remoteDb and crash with "Cannot read properties of null" inside startSync.
   *
   * Contract: any code path that calls `localDb.destroy()` MUST bracket the destroy
   * with `beginPurge()` … `try { ... } finally { endPurge() }`. Current callers:
   *   - `DatabaseWrapper.purgeDatabaseAsync` (delete-account / wipe path)
   *   - `DatabaseWrapper.restoreFromDocsAsync` (db-restore-json / db-snapshots fixture restore)
   * A future destroy site that skips the bracket will silently re-introduce the recovery race
   * documented above. (The sign-out wipe is a destroy site that deliberately does NOT use this
   * bracket, because its destruction has no end — see `beginWipeUntilReload` below.)
   *
   * Both halves of that contract are enforced, so neither is left to memory: the source tripwires
   * in `database-recovery.service.test.ts` fail the unit suite when a `localDb.destroy()` appears
   * without the bracket, and when the caller list above stops matching the methods that destroy. */
  beginPurge() { this.purgesInProgress++; }
  endPurge() { this.purgesInProgress--; }

  /* OTHER places in the client destroy a PouchDB database too, and all are outside the contract
   * above on purpose rather than by oversight: `UserDbService.discardFailedImpersonationDownloadAsync`
   * destroys a failed impersonation download (and `deletePendingImpersonationCopyAsync` deletes a
   * stopped impersonation's copy at start-up, before any database opens), and
   * `CoachExerciseSyncService.startAsync` destroys a
   * stale coach-exercise staging database inside its identity guard. None is
   * `DatabaseWrapper.localDb`, so the contract as written does not reach them, and none needs
   * it: a post-destroy "database is closed" rejection is recognised as ours by
   * `weClosedThisHandleOurselves` in `decideDatabaseErrorAction` before any recovery is begun,
   * whether or not this flag is raised. So no recovery runs, nothing reloads, `app-sentry.ts` keeps
   * it out of the unhandled-rejection report, and what is left is one deduped
   * `DatabaseClosedHandleCalled` silent-problem report for an expected condition.
   *
   * One case could not be settled by reading, and is the reason to say all this rather than
   * "it is fine": a transaction already OPEN when the store is deleted can reject as a dying
   * connection instead — an `isIdbError` but NOT an `isClosedDatabaseHandleError` — which would
   * take the recreate-the-connection path. Each site's own comment says whether it can arise
   * there. If it ever is observed, bracketing that site is the fix, and doing so means rewording
   * the contract above or giving these sites a marker of their own, because the source tripwires
   * read that wording. The whole question was recorded and then settled as `df-1261a7488c1e`.
   *
   * This paragraph sits AFTER `endPurge` deliberately. The tripwire reads the `- `Class.method``
   * bullets between `Current callers:` and `beginPurge() {` and asserts they name exactly the
   * methods that call `localDb.destroy()`, so prose about OTHER destroy sites must stay out of
   * that window. */

  /** The sign-out wipe (`prepareForReloadAsync` with `clearLocalData`) destroys the local databases
   * WITHOUT `localDb.destroy()`: it calls `indexedDB.deleteDatabase()` on every store directly,
   * including the `_pouch_*` ones whose PouchDB instances are still open and still live-syncing
   * (cancelling sync there was tried and reverted — see the NOTE in `prepareForReloadAsync`). Deleting
   * a store under an open connection makes that connection die, and a dying connection looks exactly
   * like IndexedDB corruption. So `interceptDatabaseError` used to answer it the way it answers real
   * corruption: run graceful recovery, recreate the PouchDB instances, and restart sync on every
   * database — including the user database that sign-out has just shut down, whose `remoteDb` is
   * therefore null. That produced the `StartSyncWithoutRemoteDb` silent problem, and it also re-created
   * the very stores the wipe had just deleted. It is a race, so it fires on only some sign-outs — which
   * is what makes it dangerous: it went unnoticed until it red-lit an E2E run whose commit contained no
   * application code at all.
   *
   * This flag says: from here on, the databases are being destroyed on purpose and the page is going
   * to reload, so an IndexedDB error is the expected consequence of our own wipe, not a fault to
   * recover from. It has no matching `end...` call by design — `reloadAppAsync` guarantees
   * `location.reload()` in a `finally`, so the suppression should last for the rest of this page's
   * life. There is nothing after it to un-suppress for. That is also why the sign-out wipe cannot use
   * the balanced `beginPurge()`/`endPurge()` counter above. */
  beginWipeUntilReload() { this.wipingUntilReload = true; }

  /** True while we are destroying databases ON PURPOSE — either a destroy-bracketed purge, or the
   * sign-out wipe. Both mean the same thing to a caller: an IndexedDB error right now is the expected
   * consequence of our own destruction, not a fault. Two callers rely on it, and BOTH want both kinds:
   * `interceptDatabaseError` (do not run graceful recovery) and the unhandled-rejection handler in
   * `app-sentry.ts` (do not report the resulting rejections, and do not fail the E2E browser-error
   * gate on them). It is deliberately not called `isPurging()` any more: the sign-out wipe is not a
   * purge, and a name that claimed otherwise would mislead at both call sites. */
  isIntentionalDestruction(): boolean { return this.purgesInProgress > 0 || this.wipingUntilReload; }

  /** True while a DB recovery is in flight, including the 5s post-success cooldown
   * (see attemptGracefulRecoveryAsync). On graceful-recovery failure the flag is
   * never cleared either — recoverFromDatabaseError reloads the page, so "sticky
   * true until reload" is the correct semantics. Callers can use this to suppress
   * downstream symptoms — e.g. TypeErrors thrown by code holding stale/undefined
   * state because a deeper DB helper swallowed the IDB error and returned undefined.
   * #3497. */
  isInDatabaseRecovery(): boolean { return this.startingDatabaseRecovery; }

  /** #4502: wait for the database recovery that `interceptDatabaseError` just took this
   * failure into. `RepairedInPlace` means the caller may read again; the other two answers mean
   * stop, and differ only in whether anything is coming to replace the page — see
   * `DatabaseRecoverySettlement`, and df-4ebecd016a94 for why that difference had to be told.
   * It never hangs — which is the whole point, because the caller used to be handed a promise
   * nobody held a reference to and so could never be woken at all. */
  whenDatabaseRecoverySettledAsync(): Promise<DatabaseRecoverySettlement> {
    return this.databaseRecoveryOutcome.whenSettled();
  }

  /** #4502: the ONE way to start a database recovery. Raising the in-recovery flag and opening
   * a fresh outcome for callers to wait on must always happen together: a branch that raised
   * the flag alone would hand the next waiting caller the PREVIOUS recovery's answer — the
   * database reported healthy when this recovery has not even run — and nothing would catch
   * it. Keeping both in one method makes that mistake impossible rather than merely unlikely. */
  private beginDatabaseRecovery() {
    this.startingDatabaseRecovery = true;
    this.databaseRecoveryOutcome.begin();
  }

  // Returns true if the error was intercepted and handled. The caller must then stop what it
  // was doing and wait for `whenDatabaseRecoverySettledAsync()`, which says whether the
  // database was repaired in place (read again), the page is being replaced (give up quietly),
  // or neither happened and nothing is coming (give up, and finish anything you still owe the
  // user yourself — df-4ebecd016a94).
  // #4502: this used to say "block forever", and one caller took that literally by
  // handing back a promise nobody could complete — which stranded it when the repair succeeded
  // without reloading the page.
  //
  // `origin` names the call site that handed the error in. It is OPTIONAL: only a handful of
  // the roughly forty callers can produce a closed database handle, and only those pass it.
  // Everything else keeps working unchanged and reports `unknown`. It exists because the
  // report this method files for a closed handle used to carry the message and nothing else,
  // which named no caller at all — see `reportClosedDatabaseHandleCallOnce` (#5306).
  interceptDatabaseError(error: any, origin: ClosedHandleOrigin = 'unknown'): boolean {
    // WHICH failure this is, and WHAT should be done about it, is decided in
    // `database-error-families.ts` — one place, unit-tested, no Angular (df-4d553cd0ab53).
    // Everything below is carrying that decision out. Do not re-add a guard here: the order
    // they run in is load-bearing, and stating it in two places is what this replaced.
    const action = decideDatabaseErrorAction(error, {
      intentionalDestructionIsUnderway: this.isIntentionalDestruction(),
      aRecoveryIsAlreadyStarting: this.startingDatabaseRecovery,
      msSinceLastGracefulRecovery: Date.now() - this.lastGracefulRecoveryTime,
      localStoreSafeToAbandon: (databaseId) => this.localStoreSafeToAbandon(databaseId),
    });

    if (action.kind === DatabaseErrorActionKind.NotADatabaseError) {
      return false;
    }

    // every kind is named below, and the `default` arm makes that a compile error to forget:
    // assigning an unhandled kind to `never` fails to build. That matters here because two of
    // the kinds run NO recovery, so a new one added to the enum and left out of this switch
    // would quietly take the recovery path and repair a database nobody said was broken.
    switch (action.kind) {
      case DatabaseErrorActionKind.SuppressedByOurOwnDestruction:
        // during a purge or the sign-out wipe, swallow — we are destroying these databases on
        // purpose, so the failure is expected post-destroy, not corruption (see beginPurge /
        // beginWipeUntilReload).
        //
        // #4502: no recovery runs here, so tell waiting callers to give up. Without this they
        // would be handed the answer a PREVIOUS repair left behind — a settled "the database
        // is healthy" — and would read again against databases we are deliberately deleting,
        // then report a failure that is entirely expected.
        this.databaseRecoveryOutcome.databasesAreBeingDestroyedOnPurpose();
        // a console breadcrumb rather than total silence, and a console.log rather than a
        // console.error so it does not trip the E2E unexpected-browser-error gate — it still
        // reaches the browser console log, which is the artifact this class of bug is
        // actually diagnosed from
        console.log(action.suppressionLogMessage, (error as Error)?.message ?? error);
        return true;

      case DatabaseErrorActionKind.LeaveItToTheRecoveryInFlight:
        // a recovery is already running and owns the outcome these callers are parked on
        return true;

      case DatabaseErrorActionKind.ClosedHandleWeClosedOurselves:
        // #4502: no recovery runs, so waiting callers must give up rather than be handed the
        // "repaired" answer a PREVIOUS recovery left standing — their handle is closed either way.
        // df-4ebecd016a94: and this is the ONE route that answers "no repair and no reload". Every
        // other route out of this method either repairs the database or replaces the page, so a
        // caller told to stop can rely on something else finishing what it gave up on. Here
        // nothing will, which the cold start has to know: it owns the loading splash, and the
        // splash only comes down if the cold start takes it down itself.
        this.databaseRecoveryOutcome.noRepairAndNoReloadIsComing();
        this.reportClosedDatabaseHandleCallOnce(error, origin);
        return true;

      case DatabaseErrorActionKind.ReloadThePage:
        this.beginRecoveryAndReport(action.family.silentProblemName, error);
        this.recoverFromDatabaseError(error);
        return true;

      case DatabaseErrorActionKind.ReloadBecauseItFailedAgainTooSoon:
        this.beginRecoveryAndReport(action.family.silentProblemName, error);
        console.error(action.recurrenceLogMessage);
        this.telemetry.trackEvent('database_graceful_recovery_failed', { reason: action.recurrenceTelemetryReason });
        this.recoverFromDatabaseError(error);
        return true;

      case DatabaseErrorActionKind.RecreateTheConnection:
        this.beginRecoveryAndReport(action.family.silentProblemName, error);
        // try graceful recovery first (recreate PouchDB instances), fall back to full reload
        void this.attemptGracefulRecoveryAsync(error);
        // tell the caller we took this failure; it waits on whenDatabaseRecoverySettledAsync()
        return true;

      case DatabaseErrorActionKind.SwitchToFreshLocalStore:
        this.beginRecoveryAndReport(action.family.silentProblemName, error);
        void this.switchToFreshLocalStoreAsync(error, action.family.failedDatabaseId?.(error));
        return true;

      default: {
        // unreachable: every kind above returns. If this line stops compiling, a kind was added
        // to DatabaseErrorActionKind and nothing here says what to do with it.
        const unhandled: never = action;
        // Unreachable is not the same as harmless, so this answers rather than absorbs. We have
        // said `true`, which parks every caller on the recovery outcome, and nothing below is
        // going to settle it — so settle it here with the one answer that is true whatever the
        // unknown decision was: nothing repaired anything and no reload is coming. Without it
        // this is the #4502 forever-freeze arriving through a branch nobody thought reachable.
        this.databaseRecoveryOutcome.noRepairAndNoReloadIsComing();
        this.reportSilentProblem('DatabaseRecoveryDecisionNotCarriedOut', {
          kind: (unhandled as { kind?: string })?.kind,
          message: (error as Error)?.message,
        });
        return true;
      }
    }
  }

  /** Open a recovery and, if this family reports one, file its silent problem.
   *
   * The two always happen together and in this order, for every route that recovers — which is
   * why they are here rather than repeated in each `case`. `problemName` being undefined means
   * the family files nothing, which the IndexedDB family deliberately does; see
   * `silentProblemName` in database-error-families.ts. */
  private beginRecoveryAndReport(problemName: string | undefined, error: any) {
    this.beginDatabaseRecovery();
    if (problemName !== undefined) {
      this.reportSilentProblem(problemName, { message: (error as Error)?.message });
    }
  }

  /** Report ONE late call on a database handle we had already closed, however many times that same
   * error object is delivered to us — an unhandled rejection reaches `interceptDatabaseError`
   * twice, from our own listener and from Sentry's, in an order neither side controls.
   *
   * Why it is a problem rather than an info, and why the report lives here rather than in the
   * listener: design-docs/boot-sequence.md, "A call on a handle we closed ourselves is not a
   * broken IndexedDB". That section also carries the E2E ignore-list note. */
  private reportClosedDatabaseHandleCallOnce(error: any, origin: ClosedHandleOrigin) {
    const message = (error as Error)?.message ?? error;
    // if we have already reported this exact error object, leave a breadcrumb instead of a
    // second event. console.log, not console.error, so a repeat delivery cannot trip the E2E
    // unexpected-browser-error gate on its own.
    if (typeof error === 'object' && error !== null) {
      if (this.reportedClosedDatabaseHandleErrors.has(error)) {
        console.log('Repeat delivery of a call on a database handle we had already closed:', message);
        return;
      }
      this.reportedClosedDatabaseHandleErrors.add(error);
    }
    // PouchDB's `completeReplication` attaches the replication's own result to the error before
    // it emits 'error', so `result` being there at all says the call came from inside a
    // replication rather than from app code, and `result.status` says whether that replication
    // had been cancelled. Two short, bounded fields that tell those cases apart — and unlike the
    // stack below they are readable in production.
    const replicationResult = (error as { result?: { status?: string } })?.result;
    this.reportSilentProblem('DatabaseClosedHandleCalled', {
      message,
      origin,
      viaReplication: replicationResult != null,
      replicationStatus: replicationResult?.status,
      // A DEV AND CI SIGNAL ONLY. Nobody uploads source maps for the production bundle, and
      // Sentry symbolicates only an event's own exception frames — never a string inside
      // context — so in production this is minified noise. It is kept because this condition
      // has only ever been observed in CI, where the bundle is not minified and the console
      // line this report writes carries the whole thing.
      stack: describeErrorForLog(error),
    }, { closed_handle_origin: origin });
  }

  /**
   * May the app abandon this database's silent local store without asking (#6788)?
   *
   * Any failure to find out answers false, the cautious direction: false keeps the old
   * behaviour, true gives a store up. The facts and the rule are in local-store-generation.ts.
   */
  private localStoreSafeToAbandon(databaseId: string | undefined): boolean {
    if (!databaseId) return false;
    try {
      // EVERY live wrapper on the store must agree: a second start in the same page opens a
      // second wrapper on the same store, and either one may have a save under way
      const databases = this.liveDatabasesFor(databaseId);
      if (databases.length === 0) return false;
      return databases.every(database => mayAbandonSilentLocalStore(database.silentLocalStoreFacts));
    } catch (err) {
      this.reportSilentProblem('LocalStoreAbandonCheckFailed', errorReportFields(err));
      return false;
    }
  }

  /**
   * Switch the user's silent, brand-new local store to a fresh name and reload (#6788).
   *
   * If the switch cannot be made (storage refused the once-only flag or the move), this
   * falls back to what every other silent store gets: the same-name reconnect.
   */
  private async switchToFreshLocalStoreAsync(error: any, databaseId: string | undefined) {
    // FIRST stop new reads and writes of this store. A write that landed in it after the
    // decision would be stranded in a store nothing reads again. A recovery already holding
    // the gate means one is running on this store, so leave it to that.
    const releaseHold = this.holdLiveDatabasesFor(databaseId);
    if (!releaseHold) {
      console.log('Database recovery: could not hold the silent local store for a switch — reconnecting instead');
      await this.attemptGracefulRecoveryAsync(error);
      return;
    }

    // give writes already past the gate time to settle, and send the reports so far. The
    // reload's own telemetry flush writes to this store too, parks on the hold and is given
    // up by its bound, so a ReloadTelemetryFlushGaveUp report follows every switch.
    await this.flushSentryBeforeStoreSwitchAsync();

    // ask again: a write confirmed while we waited means the store now holds something the
    // server may lack. Checked BEFORE the switch, which spends the account's one automatic try.
    let newName: string | undefined;
    if (this.localStoreSafeToAbandon(databaseId)) {
      try {
        newName = this.app.switchSilentLocalStoreWithoutAsking();
      } catch (err) {
        this.reportSilentProblem('LocalStoreAutoSwitchFailed', errorReportFields(err));
      }
    } else {
      console.log('Database recovery: the silent local store confirmed a write while the switch was prepared — keeping it');
    }
    if (newName === undefined) {
      // the reconnect needs the gate free: a held gate reads to it as a recreation in progress
      releaseHold();
      console.log('Database recovery: not switching the silent local store to a fresh name — reconnecting instead');
      await this.attemptGracefulRecoveryAsync(error);
      return;
    }

    console.log(`Database recovery: the silent local store had nothing to lose — switched to ${newName}, reloading`);
    // this page is going to be replaced; tell every waiting caller to give up quietly. The hold
    // stays on, so nothing can land in the abandoned store before the reload.
    this.databaseRecoveryOutcome.pageIsBeingReplaced();
    // the switch's own report must leave the device before the page goes
    await this.flushSentryBeforeStoreSwitchAsync();
    await this.app.reloadAppAsync({ resetToRoot: true });
  }

  /**
   * The wrappers of this database that are still in use (#6788). `allDatabases` keeps every
   * wrapper the page ever made; one whose shutdown has started is no longer read or written.
   */
  private liveDatabasesFor(databaseId: string | undefined): DatabaseWrapper[] {
    if (!databaseId || !this.dbProvider) return [];
    return this.dbProvider.allDatabases.filter(db => db.internalDatabaseIdentifier === databaseId && !db.isShuttingDown);
  }

  /**
   * Hold every live wrapper of this database for a store switch (#6788), or none: if any one
   * cannot be held (a recreation is running on it), release the others and answer undefined.
   */
  private holdLiveDatabasesFor(databaseId: string | undefined): (() => void) | undefined {
    const databases = this.liveDatabasesFor(databaseId);
    if (databases.length === 0) return undefined;
    const releases: Array<() => void> = [];
    for (const database of databases) {
      const release = database.holdOperationsForStoreSwitch();
      if (!release) {
        for (const taken of releases) taken();
        return undefined;
      }
      releases.push(release);
    }
    return () => { for (const release of releases) release(); };
  }

  /** A bounded Sentry flush around the #6788 store switch, like the one in recoverFromDatabaseError. */
  private async flushSentryBeforeStoreSwitchAsync() {
    try {
      await Sentry.flush(3000);
    } catch (err) {
      // the fresh-store switch's report rides in this same queue, so a report of this failure
      // could not leave the device either
      console.warn('Error flushing Sentry around the fresh-store switch:', err);
    }
  }

  /** Attempt graceful PouchDB recreation before falling back to full reload. */
  private async attemptGracefulRecoveryAsync(error: any) {
    // #4502: whatever happens below, this method must leave the recovery outcome settled.
    // Callers are waiting on it, and this method is started with `void`, so anything that
    // threw on the way to one of the two settle calls would become an unhandled rejection
    // with nothing left to answer them — the forever-freeze this whole change removes,
    // arriving by a different route. Settling twice is harmless (the second call is a
    // no-op), so the `finally` below is a backstop, not the normal path.
    try {
      // if recovery takes >1s, show a non-blocking "Reconnecting..." toast
      let reconnectingToast: Toast | null = null;
      const slowTimer = setTimeout(() => {
        reconnectingToast = this.toastCtrl.showToast({
          message: LL.common.reconnecting() as any,
          showCloseButton: true,
        });
      }, 1000);

      const recovered = await this.dbProvider.attemptGracefulRecoveryAsync();

      clearTimeout(slowTimer);
      if (reconnectingToast) {
        this.toastCtrl.dismissToast(reconnectingToast);
      }

      if (recovered) {
        this.lastGracefulRecoveryTime = Date.now();
        // #4502: wake every caller that was told to wait. The database is open again against
        // the same stored data, so they should read again rather than stay frozen for ever.
        this.databaseRecoveryOutcome.repaired();
        console.log('Database recovery: graceful PouchDB recreation succeeded');
        this.telemetry.trackEvent('database_graceful_recovery_success', {});
        // keep startingDatabaseRecovery=true for 5s to absorb stale errors from
        // old PouchDB instances whose in-flight operations are still completing
        setTimeout(() => { this.startingDatabaseRecovery = false; }, 5000);
        return;
      }

      // graceful recovery failed — fall back to full reload
      console.error('Database recovery: graceful recreation failed, falling back to reload');
      this.telemetry.trackEvent('database_graceful_recovery_failed', {});
      this.recoverFromDatabaseError(error);
    } finally {
      // if neither settle above ran, this recovery ended in a way nobody planned for. Tell
      // waiting callers to give up quietly, which is the cautious answer: it is what they are
      // told when the page is being replaced, so at worst they fall back to a default rather
      // than reading a database that may be in any state.
      this.databaseRecoveryOutcome.pageIsBeingReplaced();
    }
  }

  /** Call this when we detect any kind of error related to PouchDB/IndexedDB, which requires retrying > reloading > reinstalling. */
  public recoverFromDatabaseError(error: any) {
    // #4502: this page is going to be replaced, so tell every waiting caller to give up
    // quietly instead of acting on a database that is going away. One place here covers
    // every reload path: the SQLite branch, both "it failed again too soon" branches, and
    // graceful repair failing.
    this.databaseRecoveryOutcome.pageIsBeingReplaced();

    // mark that all databases need to be resynced
    this.app.clearRecentlySyncedMarks();

    // log error details locally for debugging (only report to Sentry if recovery fails via fatal error path)
    console.error('Attempting database error recovery:', {
      name: error?.name,
      message: error?.message,
      originalError: error?.originalError,
      stack: error?.stack
    });
    this.telemetry.trackEvent('database_recovery_start', {});

    // sessionStorage survives reload but is cleared when the tab closes —
    // no stale flags from previous sessions
    if (sessionStorage.getItem(kDbInRecoveryStorageKey)) {
      // previous recovery reload didn't help — IDB is persistently dead
      this.telemetry.trackEvent('database_recovery_fatal', {});

      // force the user to restart immediately
      void this.reporting()?.fatalError({ type: FatalErrorKind.DatabaseRecoveryError });

    } else {
      // mark that we are in recovery mode
      sessionStorage.setItem(kDbInRecoveryStorageKey, 'true');

      // flush telemetry and Sentry before reload to ensure events are sent
      void (async () => {
        // show loading spinner to user
        await this.app.showDatabaseRecoveryLoadingAsync();

        // wait 2 seconds to ensure user sees the loading spinner
        await sleep(2000);

        console.log('Flushing telemetry before database recovery reload...');

        try {
          // flush telemetry queue with 5 second timeout
          await timeout(5000, this.telemetry.flushAsync());
          console.log('Telemetry flushed successfully before reload');
        } catch (err) {
          console.warn('Telemetry flush timed out or failed:', err);
          // filed before the Sentry flush below, so this report goes out with it. Same split as
          // ReloadTelemetryFlushGaveUp: a timeout means the flusher is wedged in its backoff loop,
          // a throw means the flush itself failed, and they need different follow-ups (#5191)
          this.reportSilentProblem('DatabaseRecoveryTelemetryFlushGaveUp', {
            ...errorReportFields(err),
            gave_up_reason: err instanceof TimeoutError ? 'timeout' : 'flush_threw',
          });
        }

        try {
          // flush Sentry queue with 5 second timeout
          const sentryFlushed = await Sentry.flush(5000);
          if (sentryFlushed) {
            console.log('Sentry flushed successfully before reload');
          } else {
            console.warn('Sentry flush timed out after 5 seconds');
          }
        } catch (err) {
          // not reported: a report would go into the Sentry queue that just failed to send
          console.warn('Error flushing Sentry:', err);
        }

        // reload the app silently (loading will be dismissed by the reload)
        await this.app.reloadAppAsync({ resetToRoot: false });
      })();
    }
  }

  /** Mark that database error recovery is complete - we get here if successfully synced the databases. */
  public completeDatabaseRecovery() {
    if (sessionStorage.getItem(kDbInRecoveryStorageKey)) {
      console.log('Database recovery complete!');
      sessionStorage.removeItem(kDbInRecoveryStorageKey);
      this.telemetry.trackEvent('database_recovery_complete', {});
    }
  }
}
